import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Filters,
  Page,
  PageHeader,
  StatCard,
  StatCardList,
  Table,
  Toolbar,
  type Action,
} from '@evogard/ui';
import {
  PLATE_CLASSIFICATIONS,
  count,
  formatPercentage,
  loadFailureMessage,
} from '@evogard/domain';
import type {
  ConsultantSummary,
  OwnAwardGrants,
  TeamDiagramNode,
  Period,
  PlateTally,
  TeamMemberPerformance,
  TeamOverviewRow,
  TeamPerformance,
  TeamsOverview,
} from '@evogard/contract';
import { RankingSalesModal } from './RankingSalesModal.js';
import { GrantAwardModal, OWN_AWARD_GRANTS_KEY } from './GrantAwardModal.js';
import { TenantDiagram, TeamDiagram } from './TeamDiagram.js';
import { api } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  CLASSIFICATION_LABEL,
  WhyNotSettled,
  MoneyValue,
  currentPeriod,
  periodLabel,
} from './common.js';

/**
 * O painel do time.
 *
 * ## A pergunta que ele responde, e por que o ranking não respondia
 *
 * O ranking tinha um segundo cartão somando o realizado por equipe. Ele
 * respondia *qual time está na frente* e não respondia nada do que um líder
 * pergunta: **quem** produziu, **o quê**, e **quais placas**. Para ir do time à
 * pessoa, a operação rolava de volta para a tabela de cima e procurava de olho.
 *
 * Esta tela é a resposta inteira, e aquele cartão saiu junto com a ação que o
 * mostrava (18/09). Somar o mesmo time em dois lugares é como §5.6 começou.
 *
 * ## As quatro categorias estão na tabela, e "Contadas" NÃO é a soma delas
 *
 * As colunas saem de `PLATE_CLASSIFICATIONS` com o rótulo de
 * `CLASSIFICATION_LABEL` — a mesma constante de onde o servidor monta a quebra,
 * na mesma ordem, de forma que cabeçalho e célula não possam desencontrar. Uma
 * quinta classificação aparece aqui sozinha.
 *
 * Venda, troca, Bronze e reativação, uma coluna cada, mais "Contadas". As
 * quatro somam mais do que "Contadas" porque a reativação não entra na contagem
 * da casa (P5) — e é exatamente por isso que ela é uma coluna e não some: é ela
 * que explica a diferença entre o que a equipe fez e o que a casa reconhece,
 * que é a pergunta que a operação faz olhando duas telas.
 *
 * **Nenhuma soma acontece aqui.** "Contadas" vem de `rankingTallyOf`, a mesma
 * função que produz o ranking, e os totais vêm do servidor, nos cartões do
 * topo. Um `reduce` nesta tela seria a segunda implementação da contagem que
 * paga — e as duas que existiam em produção já discordavam entre si.
 *
 * ## A linha zerada FICA, e é informação
 *
 * Todo integrante do time aparece, inclusive quem não vendeu nada no mês. Uma
 * tabela só com quem pontuou mostraria uma equipe de doze como uma equipe de
 * três, e "quantos da minha equipe venderam este mês" é a primeira coisa que se
 * olha aqui. Os dois números estão no cartão, lado a lado.
 *
 * ## O perfil aparece na linha, e ele não muda número nenhum
 *
 * Quem é da base de reativação aparece com "Contadas" zero e quarenta placas —
 * é o §4.2 caso C —, e sem o selo do perfil ao lado essa linha lê como "esta
 * pessoa não vendeu". A consequência a não esquecer: essa pessoa aparece aqui e
 * **não** aparece no ranking, onde ela não disputa. O selo é o que explica a
 * diferença entre as duas telas.
 *
 * ## Quem chega aqui: quem LIDERA, quem ESTÁ, e quem alcança tudo
 *
 * São três ações, e as duas estreitas somam. `ver` é o piso e dá os times que
 * a pessoa lidera; `ver_meu_time` acrescenta a equipe em que ela está;
 * `ver_qualquer` dá a base inteira. Quem tem o piso e nenhum time ao alcance
 * cai no estado vazio, que diz **o que falta** — não é 403, e a distinção
 * importa: a permissão existe, o que falta é cadastro.
 *
 * ## O agrupamento é pela composição de HOJE, e a tela diz isso
 *
 * `sales_consultants.team_id` não tem vigência. Uma placa de março vendida por
 * quem mudou de equipe em julho aparece no painel do time **atual** dela. Está
 * na descrição do cartão em vez de escondido: número que não se sustenta e não
 * se anuncia é pior do que número ausente.
 *
 * ## A coluna de ações também reparte a premiação do líder
 *
 * Com `sales.commissions:conceder_premiacao_apenas_a_membros_do_time` — pelo
 * grupo ou por exceção individual —, a linha de cada liderado **direto** (um
 * membro de um time que a pessoa lidera) ganha "Repartir premiação", que abre
 * `GrantAwardModal`. A linha da própria pessoa nunca ganha: o líder não
 * concede a si mesmo. Quem pode receber
 * vem do servidor (`ownAwardGrants`), com as mesmas travas da gravação — não é
 * deduzido aqui.
 *
 * ## A visão geral, para quem acompanha sem ser do time
 *
 * Com `sales.team_performance:ver_visao_geral` (28/09/2026) a tela não abre
 * num time: abre numa tabela com TODOS os times da base, uma linha por time
 * com o total do ramo, e num diagrama geral com a base na raiz — "Evogard
 * Ceará", e os times de nível 1 saindo dela. Cada linha abre o painel de
 * sempre, ou o diagrama daquele time, e "Visão geral" volta.
 */
export function TeamPanelScreen() {
  const { data: context } = useSessionContext();
  const [period, setPeriod] = useState<Period>(currentPeriod());
  /*
   * `''` é "o meu time", e a rota recebe o campo AUSENTE — o servidor responde
   * o primeiro de `options`, que vem ordenada por nível na cadeia: o time da
   * própria pessoa, depois os de quem responde a ela. Nunca foi "todos": um
   * painel de todos os times somaria equipes diferentes numa linha só, que é o
   * cartão que acabou de sair do ranking.
   */
  const [teamId, setTeamId] = useState('');
  /*
   * A TRILHA — os times acima do que está aberto, do mais alto para o mais
   * perto (23/09/2026). Abrir um time lá embaixo guarda o caminho inteiro até
   * ele, e "Voltar" sobe UM nível por vez: em A→B→C, abrir C e voltar dá B, e
   * voltar de novo dá A. Trocar de time pelo filtro recomeça a trilha — o
   * caminho que ela guardava deixou de ser o caminho até ali.
   */
  const [trail, setTrail] = useState<{ id: number; nome: string }[]>([]);
  /*
   * Tabela ou diagrama — a MESMA resposta, desenhada de dois jeitos. O
   * diagrama é a estrutura (quem está sob quem); a tabela, os números.
   */
  const [view, setView] = useState<'tabela' | 'diagrama'>('tabela');
  /*
   * O que a pessoa via na visão geral quando abriu um time — a tabela ou o
   * diagrama geral —, para "Voltar" devolver exatamente ali. Nulo quando o
   * time não foi aberto a partir da visão geral.
   */
  const [returnTo, setReturnTo] = useState<'tabela' | 'diagrama' | null>(null);
  const tenantId = context?.currentTenant.id;
  /*
   * A visão geral é o estado de "nenhum time escolhido" para quem tem a ação —
   * no lugar do "o meu time" de todo mundo. Esconder não protege: a rota exige
   * a mesma ação no decorador.
   */
  const overviewAllowed = can(context, 'sales.team_performance', 'ver_visao_geral');
  const inOverview = overviewAllowed && teamId === '';

  const overviewQuery = useQuery({
    queryKey: ['comercial', 'painel-do-time', 'visao-geral', tenantId, period],
    enabled: context !== undefined && inOverview,
    queryFn: async () => {
      const r = await api.sales.teamsOverview({ query: { period } });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'a visão geral dos times'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === tenantId ? previous : undefined,
  });

  const panelQuery = useQuery({
    queryKey: ['comercial', 'painel-do-time', tenantId, period, teamId],
    enabled: context !== undefined && !inOverview,
    queryFn: async () => {
      const r = await api.sales.teamPerformance({
        query: { period, teamId: teamId ? Number(teamId) : undefined },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'o painel deste time'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const panel = inOverview ? undefined : panelQuery.data;
  const overview = inOverview ? overviewQuery.data : undefined;
  /* A troca entre tabela e diagrama existe nos dois modos. */
  const hasDiagram = inOverview ? overview !== undefined : Boolean(panel?.diagram);
  /* Abrir um time a partir da visão geral — a tabela ou o diagrama dele. */
  const openTeam = (id: number, as: 'tabela' | 'diagrama') => {
    setReturnTo(view);
    setTrail([]);
    setTeamId(String(id));
    setView(as);
  };
  /* Volta à visão geral no modo em que ela estava — tabela ou diagrama geral. */
  const backToOverview = () => {
    setTrail([]);
    setTeamId('');
    setView(returnTo ?? 'tabela');
    setReturnTo(null);
  };
  const teamOptions = inOverview
    ? (overview?.teams ?? []).map((row) => row.team)
    : (panel?.options ?? []);

  return (
    <Page measure="cheia">
      <PageHeader
        title="Painel do time"
        actions={
          hasDiagram || (overviewAllowed && !inOverview) ? (
            <>
              {overviewAllowed && !inOverview && (
                <Button variant="discreto" icon="seta-esquerda" onClick={backToOverview}>
                  Visão geral
                </Button>
              )}
              {hasDiagram && (
                <Button
                  variant="contorno"
                  icon={view === 'tabela' ? 'pessoas' : 'documento'}
                  onClick={() => setView(view === 'tabela' ? 'diagrama' : 'tabela')}
                >
                  {view === 'tabela'
                    ? inOverview
                      ? 'Ver diagrama geral'
                      : 'Ver diagrama'
                    : 'Ver tabela'}
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {inOverview &&
          'Todos os times da base na competência, cada um com o total do ramo dele, calculado no servidor.'}
      </PageHeader>

      {/*
       * O seletor de time só aparece com mais de uma opção.
       *
       * Liderar dois times é representável e acontece; liderar um é o caso
       * comum, e um campo com uma opção só é a tela perguntando algo que já
       * está respondido. Quem tem gente abaixo na cadeia de superior recebe os
       * times dela aqui, e com a ação larga `options` é a base inteira — nos
       * dois casos o campo aparece sozinho, sem nenhuma linha decidindo isso
       * aqui.
       *
       * A competência não tem vazio, e o "Limpar" do painel aplica o vazio: ela
       * cai para o mês de hoje, que é o estado em que a tela nasce, e o `help`
       * diz isso.
       */}
      <Toolbar
        toolbar={
          <Filters
            /*
             * O time do campo é o que a tela está MOSTRANDO, e não o que a
             * pessoa escolheu.
             *
             * Enquanto ninguém escolhe, `teamId` é `''` e quem decide é o
             * servidor — e um seletor sem opção vazia precisa de um valor que
             * exista na lista, senão o navegador desenha a primeira opção e o
             * estado diz outra coisa. `board.team.id` é exatamente o que foi
             * desenhado.
             */
            values={{
              period,
              teamId:
                teamId || (overviewAllowed ? '' : String(panel?.board?.team.id ?? '')),
            }}
            onApply={(values) => {
              const chosen = String(values.period ?? '');
              const nextTeam = String(values.teamId ?? '');
              setPeriod((chosen || currentPeriod()) as Period);
              if (nextTeam !== teamId) {
                /* Saindo da visão geral pelo filtro, o "Voltar" volta a ela. */
                if (inOverview && nextTeam !== '') setReturnTo(view);
                setView('tabela');
              }
              setTeamId(nextTeam);
              setTrail([]);
            }}
            fields={[
              {
                type: 'periodo',
                name: 'period',
                label: 'Competência',
                help: 'Vazio volta ao mês de hoje.',
              },
              /*
               * Com a visão geral, o vazio É uma resposta: "todos os times".
               * O campo aparece sempre, e o `required` cai.
               */
              overviewAllowed && {
                type: 'selecao' as const,
                name: 'teamId',
                label: 'Time',
                placeholder: 'Todos os times',
                help: 'Vazio é a visão geral de todos os times da base.',
                options: teamOptions.map((t) => ({ value: String(t.id), label: t.nome })),
              },
              !overviewAllowed &&
                (panel?.options.length ?? 0) > 1 && {
                  type: 'selecao' as const,
                  name: 'teamId',
                  label: 'Time',
                  /*
                   * Sem opção vazia: não existe painel de todos os times, e a
                   * vazia oferecia uma resposta que a rota não dá. Ela dizia "O
                   * primeiro da lista", que descrevia a implementação do servidor
                   * em vez do quadro que a pessoa veria — e passou a descrevê-la
                   * errado no dia em que a lista ganhou os times da cadeia
                   * abaixo, porque "o primeiro" deixou de ser o time dela.
                   */
                  required: true,
                  help: 'Um time por vez, do seu para os de quem responde a você.',
                  options: (panel?.options ?? []).map((t) => ({
                    value: String(t.id),
                    label: t.nome,
                  })),
                },
            ]}
          />
        }
      />

      {inOverview && overviewQuery.isError && (
        <Alert tone="erro">{overviewQuery.error.message}</Alert>
      )}

      {inOverview && overviewQuery.isPending && !overviewQuery.isError && (
        <Card title="Carregando…">
          <span className="esqueleto block h-4 w-2/3" />
          <span className="esqueleto mt-2 block h-4 w-1/2" />
        </Card>
      )}

      {overview !== undefined && view === 'tabela' && (
        <TeamsOverviewTable overview={overview} onOpenTeam={openTeam} />
      )}

      {overview !== undefined && view === 'diagrama' && (
        <Card
          title={`Diagrama geral de ${overview.tenant.nome}`}
          descricao="A base à esquerda, e dela sai o líder de cada time de nível 1; daí em diante, o mesmo desenho do diagrama de um time. Abrir mostra o diagrama daquele time, e Voltar traz de volta para cá. Arraste para mover, use a roda para aproximar. Os números estão na tabela."
        >
          {overview.diagram.length === 0 ? (
            <EmptyState icon="pessoas" title="Esta base não tem time nenhum.">
              Os times são cadastrados em{' '}
              <strong className="font-medium text-texto">
                Comercial › Consultores e times
              </strong>
              .
            </EmptyState>
          ) : (
            <TenantDiagram
              key={overview.tenant.nome}
              tenantName={overview.tenant.nome}
              teams={overview.diagram}
              onOpenTeam={(id) => openTeam(id, 'diagrama')}
            />
          )}
        </Card>
      )}

      {!inOverview && panelQuery.isError && (
        <Alert tone="erro">{panelQuery.error.message}</Alert>
      )}

      {!inOverview && panelQuery.isPending && !panelQuery.isError && (
        <Card title="Carregando…">
          <span className="esqueleto block h-4 w-2/3" />
          <span className="esqueleto mt-2 block h-4 w-1/2" />
        </Card>
      )}

      {/*
       * A lista vazia de times não é falha e não é 403: a pessoa tem a
       * permissão, e o cadastro é que não a põe como líder de nada — e quem não
       * lidera nada não desce a cadeia, por mais gente que tenha abaixo. As duas
       * respostas mandam para lugares diferentes — uma é pedir acesso, a outra é
       * pedir para o cadastro apontar o líder do time —, e é por isso que a tela
       * diz qual é qual.
       */}
      {panel !== undefined && panel.board === null && (
        <EmptyState icon="pessoas" title="Nenhum time desta base está ao seu alcance.">
          Este painel abre a equipe que você <strong>lidera</strong>, as equipes de quem{' '}
          <strong>responde a você</strong> na cadeia de superior e, com a permissão
          própria, a equipe em que você <strong>está</strong>. A cadeia só desce a partir
          de quem lidera um time. A permissão de abrir a tela está concedida — o que falta
          é o cadastro: apontar quem lidera o time, ou vincular você a um, em{' '}
          <strong className="font-medium text-texto">
            Comercial › Consultores e times
          </strong>
          . Quem precisa ver qualquer equipe da base recebe a ação larga do mesmo recurso.
        </EmptyState>
      )}

      {view === 'diagrama' && panel?.diagram && (
        <Card
          title={`Diagrama de ${panel.diagram.team.nome}`}
          descricao="O líder à esquerda, e uma linha para cada pessoa que responde a ele. Quem lidera outro time leva o selo, e as pessoas daquele time saem dele. Arraste para mover, use a roda para aproximar. Os números estão na tabela."
        >
          <TeamDiagram
            key={panel.diagram.team.id}
            root={panel.diagram}
            onOpenTeam={(id) => {
              const root = panel.diagram!;
              if (id === root.team.id) return;
              setTrail([...trail, ...pathTo(root, id)]);
              setTeamId(String(id));
            }}
            /*
             * No topo da trilha, quem veio da visão geral volta para ela — no
             * modo em que estava. Sem ela, o topo não tem para onde voltar.
             */
            back={
              trail.length === 0
                ? overviewAllowed
                  ? {
                      label:
                        returnTo === 'diagrama'
                          ? 'o diagrama geral'
                          : 'a visão geral dos times',
                      onBack: backToOverview,
                    }
                  : undefined
                : {
                    label: trail[trail.length - 1]!.nome,
                    onBack: () => {
                      const parent = trail[trail.length - 1]!;
                      setTrail(trail.slice(0, -1));
                      setTeamId(String(parent.id));
                    },
                  }
            }
          />
        </Card>
      )}

      {view === 'tabela' && panel?.board?.estado === 'nao_apuravel' && (
        <WhyNotSettled
          title={`O painel de ${panel.board.team.nome} em ${periodLabel(period)} não é apurável.`}
          reasons={panel.board.reasons}
        >
          <p className="mt-1">
            Nenhuma tabela é mostrada abaixo, e isso é deliberado: uma placa sem categoria
            não tem coluna para onde ir, e somá-la fora da quebra daria um total que não
            fecha com as colunas acima dele. O motivo é medido sobre as placas{' '}
            <strong className="font-medium text-texto">deste time</strong> — o painel de
            outra equipe continua respondendo.
          </p>
        </WhyNotSettled>
      )}

      {view === 'tabela' && panel?.board?.estado === 'apurado' && (
        <TeamMeasured
          board={panel.board}
          onOpenTeam={(id) => {
            /* Da tabela "Times abaixo", o filho é sempre um nível abaixo. */
            setTrail([...trail, panel.board!.team]);
            setTeamId(String(id));
          }}
        />
      )}
    </Page>
  );
}

/**
 * A visão geral: os totais da base e uma linha por time.
 *
 * O total de cada linha é o do RAMO — o time e todos os de baixo —, o mesmo
 * número dos cartões quando o time é aberto. Por isso as linhas não se somam:
 * a de cima já contém as de baixo, e o recuo por nível é o que diz isso. Nenhum
 * número é somado aqui; os da base também vêm do servidor.
 */
function TeamsOverviewTable({
  overview,
  onOpenTeam,
}: {
  overview: TeamsOverview;
  onOpenTeam: (teamId: number, as: 'tabela' | 'diagrama') => void;
}) {
  const { tenant } = overview;
  const tenantTotals = tenant.figures.estado === 'apurado' ? tenant.figures.totals : null;
  const totalsOf = (row: TeamOverviewRow) =>
    row.figures.estado === 'apurado' ? row.figures.totals : null;

  return (
    <div className="flex flex-col gap-5">
      {tenantTotals !== null ? (
        <StatCardList>
          <StatCard
            icon="carrinho"
            label="Vendas contadas"
            value={tenantTotals.counted}
            support={`de ${count(tenantTotals.counted + tenantTotals.ignored, 'placa no mês', 'placas no mês')}`}
            footer={PLATE_CLASSIFICATIONS.filter((c) => c !== 'reativacao')
              .map((c) => `${tally(tenantTotals.plates, c)} ${CLASSIFICATION_LABEL[c]}`)
              .join(' · ')}
          />
          <StatCard
            icon="atualizar"
            label="Reativações"
            value={tally(tenantTotals.plates, 'reativacao')}
            support="fora da contagem da casa"
          />
          <StatCard
            icon="dinheiro"
            label="Valor contado"
            value={<MoneyValue value={tenantTotals.value} />}
            support="só as placas que contaram"
          />
          <StatCard
            icon="pessoas"
            label="Times"
            value={overview.teams.length}
            support={`${count(tenantTotals.members, 'integrante', 'integrantes')} em algum time`}
          />
        </StatCardList>
      ) : (
        <WhyNotSettled
          title={`Os totais de ${tenant.nome} em ${periodLabel(overview.period)} não são apuráveis.`}
          reasons={tenant.figures.estado === 'nao_apuravel' ? tenant.figures.reasons : []}
        >
          <p className="mt-1">
            O motivo é medido time a time: a linha de um time sem o problema continua com
            os números dela na tabela abaixo.
          </p>
        </WhyNotSettled>
      )}

      <Card
        title={`Times de ${tenant.nome}`}
        descricao={`Cada linha soma o time e todos os times abaixo dele, com o líder junto — o mesmo número dos cartões quando o time é aberto. Por isso as linhas não se somam: a de cima já contém as recuadas abaixo dela. O time agrupa pela composição de hoje. As quatro categorias somam mais do que "Contadas": a reativação não entra na contagem da casa.`}
        bleed
      >
        <Table
          label={`Todos os times de ${tenant.nome} na competência`}
          items={overview.teams}
          rowKey={(row) => String(row.team.id)}
          empty="Esta base não tem time nenhum cadastrado. Os times são cadastrados em Comercial › Consultores e times."
          rowTitle={(row) => row.team.nome}
          rowSubtitle={(row) =>
            row.leader === null ? 'Sem líder' : `Líder: ${row.leader.nome}`
          }
          rowIcon="pessoas"
          rowActions={(row): Action[] => [
            {
              label: `Abrir o painel de ${row.team.nome}`,
              icon: 'olho',
              run: () => onOpenTeam(row.team.id, 'tabela'),
            },
            {
              label: `Ver o diagrama de ${row.team.nome}`,
              icon: 'pessoas',
              run: () => onOpenTeam(row.team.id, 'diagrama'),
            },
          ]}
          columns={[
            {
              title: 'Time',
              alignment: 'inicio' as const,
              minWidth: '16rem',
              cell: (row) => {
                const totals = totalsOf(row);
                return (
                  /*
                   * O recuo é o nível na árvore, e é ele que diz que a linha
                   * de baixo já está dentro da de cima.
                   */
                  <div
                    className="min-w-0"
                    style={{ paddingLeft: `${row.depth * 1.25}rem` }}
                  >
                    <p className="truncate">
                      {row.depth > 0 && (
                        <span aria-hidden className="text-texto-suave">
                          └{' '}
                        </span>
                      )}
                      {row.team.nome}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-texto-suave">
                      Nível {row.depth + 1} ·{' '}
                      {row.leader === null ? 'Sem líder' : `Líder: ${row.leader.nome}`}
                      {totals !== null &&
                        ` · ${count(totals.members, 'integrante', 'integrantes')}`}
                    </p>
                    {row.figures.estado === 'nao_apuravel' && (
                      <div className="mt-1">
                        <Badge tone="alerta">Não apurável — venda sem categoria</Badge>
                      </div>
                    )}
                  </div>
                );
              },
            },
            ...PLATE_CLASSIFICATIONS.map((classification) => ({
              title: CLASSIFICATION_LABEL[classification],
              alignment: 'fim' as const,
              cell: (row: TeamOverviewRow) => {
                const totals = totalsOf(row);
                return totals === null ? (
                  <Unsettled />
                ) : (
                  <Tally value={tally(totals.plates, classification)} />
                );
              },
            })),
            {
              title: 'Contadas',
              alignment: 'fim',
              cell: (row) => {
                const totals = totalsOf(row);
                return totals === null ? (
                  <Unsettled />
                ) : (
                  <span className="font-medium tabular-nums">{totals.counted}</span>
                );
              },
            },
            {
              title: 'Valor',
              alignment: 'fim',
              cell: (row) => {
                const totals = totalsOf(row);
                return totals === null ? (
                  <Unsettled />
                ) : (
                  <MoneyValue value={totals.value} />
                );
              },
            },
          ]}
        />
      </Card>
    </div>
  );
}

/** A célula de uma linha que não apurou: sem número, e dizendo por quê. */
function Unsettled() {
  return (
    <span className="text-texto-suave" title="Não apurável">
      —
    </span>
  );
}

/** O painel apurado: os cartões do topo e a tabela dos integrantes. */
function TeamMeasured({
  board,
  onOpenTeam,
}: {
  board: Extract<NonNullable<TeamPerformance['board']>, { estado: 'apurado' }>;
  /** Abre o painel de um time filho — ele está no alcance de quem olha. */
  onOpenTeam: (teamId: number) => void;
}) {
  const { data: context } = useSessionContext();
  /*
   * A ação das vendas, e ela decide se a linha tem gatilho. Esconder não
   * protege — quem protege é o serviço, que confere a ação **e** o alcance de
   * time antes de responder. Isto evita um botão que abre um 403, que é a tela
   * afirmando o que o código não cumpre.
   */
  const canSeeSales = can(context, 'sales.team_performance', 'ver_vendas');
  const [detailing, setDetailing] = useState<ConsultantSummary | null>(null);

  /*
   * Repartir a própria premiação — e a linha só ganha o gatilho quando o
   * servidor diz que ela é alcançável.
   *
   * O time da tabela é `team_id`; a concessão alcança a cadeia a UM degrau.
   * `directMemberIds` é a mesma pergunta que o serviço faz ao gravar, então a
   * linha que tem botão é a linha que aceita. A própria pessoa nunca está
   * nela: o líder não concede a si mesmo, o que sobra para ele é o resto.
   */
  const canGrant = can(
    context,
    'sales.commissions',
    'conceder_premiacao_apenas_a_membros_do_time',
  );
  const [granting, setGranting] = useState<ConsultantSummary | null>(null);
  const tenantId = context?.currentTenant.id;
  const mineQuery = useQuery({
    queryKey: [...OWN_AWARD_GRANTS_KEY, tenantId],
    enabled: canGrant,
    queryFn: async () => {
      const r = await api.sales.ownAwardGrants();
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'as suas premiações de líder'));
      }
      return r.body;
    },
  });
  const mine = mineQuery.data;
  const grantable = (m: TeamMemberPerformance) =>
    mine !== undefined &&
    mine.awards.length > 0 &&
    m.consultant.id !== mine.consultantId &&
    mine.directMemberIds.includes(m.consultant.id);
  /** O percentual já concedido a esta pessoa, somado entre as premiações ativas. */
  const grantedTo = (m: TeamMemberPerformance) =>
    (mine?.awards ?? [])
      .flatMap((a) => a.grants)
      .filter((g) => g.memberConsultantId === m.consultant.id && g.archivedAt === null);

  const { totals } = board;

  return (
    <div className="flex flex-col gap-5">
      {/*
       * As quatro categorias vivem AQUI, e não num rodapé da tabela.
       *
       * `Table` não tem linha de total, e dar uma a ela por causa desta tela
       * seria mexer numa peça compartilhada — com o modo de cartão do telefone
       * junto, onde "a linha de baixo" não existe como conceito. Os cartões são
       * a forma que esta casa já usa para publicar total.
       *
       * Nenhum destes números é somado nesta tela. Todos vêm de `totals`, que o
       * servidor monta — e o valor é o caso que um `reduce` com `?? 0`
       * quebraria calado, porque ele herda o desconhecido de qualquer
       * integrante e sairia para menos com cara de total exato.
       */}
      <StatCardList>
        <StatCard icon="carrinho" label="Vendas contadas" value={totals.counted} />
        {/*
         * Cartão próprio, e não uma linha de rodapé do primeiro: é o número
         * que explica a diferença entre o que a equipe fez e o que a casa
         * conta. Dentro do cartão de cima ele leria como parte do que contou.
         */}
        <StatCard
          icon="atualizar"
          label="Reativações"
          value={tally(totals.plates, 'reativacao')}
        />
        <StatCard
          icon="dinheiro"
          label="Valor contado"
          value={<MoneyValue value={totals.value} />}
        />
        <StatCard icon="pessoas" label="Integrantes" value={totals.members} />
      </StatCardList>

      <Card title={board.team.nome} bleed>
        <Table
          label={`Integrantes de ${board.team.nome} na competência`}
          items={board.members}
          rowKey={(m) => String(m.consultant.id)}
          empty="Este time não tem integrante nenhum no cadastro. Isto é uma resposta sobre o cadastro, não sobre o mês."
          rowTitle={(m) => m.consultant.user.nome}
          rowIcon="comercial"
          rowActions={(m): Action[] => [
            ...(canSeeSales
              ? [
                  {
                    label: `Ver as ${m.counted + m.ignored} vendas de ${m.consultant.user.nome}`,
                    icon: 'olho' as const,
                    run: () => setDetailing(m.consultant),
                  },
                ]
              : []),
            ...(grantable(m)
              ? [
                  {
                    label:
                      grantedTo(m).length === 0
                        ? `Repartir premiação com ${m.consultant.user.nome}`
                        : `Editar a premiação repartida com ${m.consultant.user.nome}`,
                    icon: 'dinheiro' as const,
                    run: () => setGranting(m.consultant),
                  },
                ]
              : []),
          ]}
          columns={[
            {
              title: 'Integrante',
              alignment: 'inicio' as const,
              minWidth: '14rem',
              cell: (m) => (
                <div className="min-w-0">
                  <p className="truncate">{m.consultant.user.nome}</p>
                  <Marks member={m} />
                  {grantedTo(m).map((g) => (
                    <p key={g.id} className="mt-1 text-xs tabular-nums text-texto-suave">
                      {g.title !== null ? `${g.title} · ` : ''}recebe{' '}
                      {formatPercentage(g.percentage)}% da sua premiação
                    </p>
                  ))}
                </div>
              ),
            },
            /*
             * As quatro colunas saem de `PLATE_CLASSIFICATIONS`, e não de
             * quatro blocos copiados.
             *
             * É a mesma constante de onde o servidor monta a quebra, na mesma
             * ordem — então a coluna e a célula não podem desencontrar. E uma
             * quinta classificação aparece aqui sozinha, com o rótulo que
             * `CLASSIFICATION_LABEL` já é obrigado a ter: o `satisfies` de lá
             * quebra a compilação até alguém escrevê-lo.
             */
            ...PLATE_CLASSIFICATIONS.map((classification) => ({
              title: CLASSIFICATION_LABEL[classification],
              alignment: 'fim' as const,
              cell: (m: TeamMemberPerformance) => (
                <Tally value={tally(m.plates, classification)} />
              ),
            })),
            {
              title: 'Contadas',
              alignment: 'fim',
              cell: (m) => <span className="font-medium tabular-nums">{m.counted}</span>,
            },
            {
              title: 'Valor',
              alignment: 'fim',
              cell: (m) => <MoneyValue value={m.value} />,
            },
          ]}
        />
      </Card>

      {/*
       * Os times ABAIXO, uma linha por time filho com o total do ramo inteiro
       * (23/09/2026). Desde 28/09 o líder do filho entra aqui também: ele é
       * integrante deste time e tem a linha dele na tabela acima, e a linha do
       * ramo mostra as placas dele. Os cartões o contam uma vez só, então as
       * duas tabelas somadas podem passar do total.
       */}
      {board.subteams.length > 0 && (
        <Card title="Times abaixo" bleed>
          <Table
            label={`Times abaixo de ${board.team.nome}`}
            items={board.subteams}
            rowKey={(b) => String(b.team.id)}
            empty="Nenhum time abaixo deste."
            rowTitle={(b) => b.team.nome}
            rowSubtitle={(b) =>
              b.leader === null ? 'Sem líder' : `Líder: ${b.leader.nome}`
            }
            rowIcon="pessoas"
            rowActions={(b): Action[] => [
              {
                label: `Abrir o painel de ${b.team.nome}`,
                icon: 'olho',
                run: () => onOpenTeam(b.team.id),
              },
            ]}
            columns={[
              {
                title: 'Time',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (b) => (
                  <div className="min-w-0">
                    <p className="truncate">{b.team.nome} e abaixo</p>
                    <p className="mt-0.5 truncate text-xs text-texto-suave">
                      {b.leader === null ? 'Sem líder' : `Líder: ${b.leader.nome}`} ·{' '}
                      {count(b.totals.members, 'integrante', 'integrantes')}
                    </p>
                  </div>
                ),
              },
              ...PLATE_CLASSIFICATIONS.map((classification) => ({
                title: CLASSIFICATION_LABEL[classification],
                alignment: 'fim' as const,
                cell: (b: (typeof board.subteams)[number]) => (
                  <Tally value={tally(b.totals.plates, classification)} />
                ),
              })),
              {
                title: 'Contadas',
                alignment: 'fim',
                cell: (b) => (
                  <span className="font-medium tabular-nums">{b.totals.counted}</span>
                ),
              },
              {
                title: 'Valor',
                alignment: 'fim',
                cell: (b) => <MoneyValue value={b.totals.value} />,
              },
            ]}
          />
        </Card>
      )}

      {mineQuery.isError && <Alert tone="erro">{mineQuery.error.message}</Alert>}

      {/*
       * Quem tem a permissão e não vê o botão precisa saber POR QUÊ.
       *
       * A permissão é a porta; o alcance é cadastro, e três cadastros podem
       * faltar. Sem este aviso a coluna de ações simplesmente não oferece nada,
       * e a pessoa conclui que a exceção não funcionou — foi o relato de
       * 23/09, com a exceção certa gravada e a premiação ainda `proprio`.
       */}
      {canGrant && mine !== undefined && <WhyNoGrant mine={mine} />}

      {granting && mine && (
        <GrantAwardModal
          member={granting}
          mine={mine}
          onClose={() => setGranting(null)}
        />
      )}

      {detailing && (
        <RankingSalesModal
          consultant={detailing}
          period={board.period}
          source="time"
          open
          onClose={() => setDetailing(null)}
        />
      )}
    </div>
  );
}

/**
 * O que falta no cadastro para a repartição aparecer — ou nada, quando nada falta.
 *
 * As frases vêm da ordem das perguntas do serviço: ser consultor, ter premiação
 * de líder, ter liderado alcançável. Cada uma manda para um lugar diferente, e
 * é por isso que são três e não um "não disponível".
 */
function WhyNoGrant({ mine }: { mine: OwnAwardGrants }) {
  if (mine.consultantId === null) {
    return (
      <Alert tone="informacao" title="Você não é consultor nesta base.">
        A permissão de repartir está concedida, mas a premiação de líder pertence a um
        consultor, e o seu usuário não tem vínculo de consultor aqui. O vínculo é feito em{' '}
        <strong className="font-medium">
          Comercial › Consultores, times e hierarquia
        </strong>
        .
      </Alert>
    );
  }
  if (mine.awards.length === 0) {
    return (
      <Alert tone="informacao" title="Você não tem premiação de líder para repartir.">
        A permissão está concedida, mas só se reparte uma premiação de recorrência cuja
        base seja <strong className="font-medium">dos liderados diretos</strong>. Uma
        premiação sobre a sua própria carteira não se reparte. Quem administra a
        remuneração cadastra a de líder na sua ficha de consultor.
      </Alert>
    );
  }
  if (mine.directMemberIds.length === 0) {
    return (
      <Alert tone="informacao" title="Ninguém está ao alcance da repartição.">
        A sua premiação de líder alcança quem responde{' '}
        <strong className="font-medium">diretamente a você</strong> — os membros de um
        time que você lidera —, e hoje ninguém responde. Se as pessoas já estão no seu
        time, o cadastro é anterior a 23/09: abra o time em{' '}
        <strong className="font-medium">
          Comercial › Consultores, times e hierarquia
        </strong>{' '}
        e salve com a data desde quando elas estão com você.
      </Alert>
    );
  }
  return null;
}

/**
 * O caminho da raiz do diagrama até o PAI de um time — os times que ficam na
 * trilha quando ele é aberto. Vazio quando o time não está na árvore, e aí a
 * trilha não cresce com um caminho inventado.
 */
function pathTo(root: TeamDiagramNode, id: number): { id: number; nome: string }[] {
  type Step = { id: number; nome: string };
  function walk(node: TeamDiagramNode, above: Step[]): Step[] | null {
    const here = [...above, node.team];
    for (const child of node.children) {
      if (child.team.id === id) return here;
      const deeper = walk(child, here);
      if (deeper !== null) return deeper;
    }
    return null;
  }
  return walk(root, []) ?? [];
}

/**
 * Quantas placas de uma categoria, numa quebra que vem do servidor.
 *
 * A quebra é uma lista de `{ classification, quantity }` porque o valor de um
 * enum não pode ser nome de campo no contrato — identificador nesta casa é em
 * inglês, e a versão com `venda`/`troca` como chave foi reprovada por
 * `make nomes`.
 *
 * **O `?? 0` é a única decisão desta função, e ela é consciente.** O servidor
 * manda as quatro sempre (`breakdownOf`), e o contrato cobra isso em
 * `sales.test.ts`; o tipo da rede é `z.array` e não consegue garanti-lo
 * sozinho. Zero é a leitura certa para uma categoria ausente **aqui**, onde o
 * que se conta são placas — e o alternativo, um `!`, estouraria a tela inteira
 * por uma célula.
 */
function tally(plates: readonly PlateTally[], classification: string): number {
  return plates.find((p) => p.classification === classification)?.quantity ?? 0;
}

/**
 * O número de uma categoria — e o zero fica cinza.
 *
 * Numa tabela de quatro colunas de contagem, o zero é a maioria das células, e
 * em preto ele compete com os números que a pessoa veio ler. Cinza não é
 * esconder: o valor continua escrito e continua sendo um fato.
 */
function Tally({ value }: { value: number }) {
  return (
    <span className={value === 0 ? 'tabular-nums text-texto-suave' : 'tabular-nums'}>
      {value}
    </span>
  );
}

/**
 * Os selos da linha — o perfil, e "parou de vender".
 *
 * ## Por que o perfil é um selo e não uma coluna
 *
 * Ele não é um número e não se compara entre linhas: ele **qualifica** o número
 * ao lado. Numa coluna própria, ficaria repetido em toda linha de um time
 * inteiro de consultores comuns, ocupando largura para dizer o esperado; como
 * selo, ele aparece só quando muda a leitura.
 *
 * `comum` não desenha nada de propósito: é o caso normal, e um selo em toda
 * linha treinaria quem lê a ignorá-lo — justo o oposto do que os outros dois
 * precisam.
 */
function Marks({ member }: { member: TeamMemberPerformance }) {
  const marks = [
    member.profile === 'base_reativacao' && (
      /*
       * O selo que impede a linha de ser lida errado. Quem é da base de
       * reativação aparece com "Contadas" zero e quarenta placas — §4.2 caso C
       * —, e sem ele essa linha diz "esta pessoa não vendeu". É também o que
       * explica por que ela não está no ranking: lá ela não disputa.
       */
      <Badge key="perfil" tone="informacao">
        Base de reativação
      </Badge>
    ),
    !member.active && (
      /*
       * `ativo = false` é "parou de vender", e não "este vínculo nunca deveria
       * ter existido" (esse é `archived_at`, e não chega aqui). A pessoa
       * continua dona das placas antigas, então a linha dela de março continua
       * certa em setembro — o selo é o que impede alguém de ler o zero de hoje
       * como desempenho.
       */
      <Badge key="ativo" tone="neutro">
        Parou de vender
      </Badge>
    ),
  ].filter(Boolean);

  if (marks.length === 0) return null;
  return <div className="mt-1 flex flex-wrap gap-1">{marks}</div>;
}
